import React from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LangContext";
import { t } from "@/lib/i18n";
import "@/lib/i18n_p1";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { LogOut, MessageSquare, BookOpen, Bookmark, Settings, LayoutGrid, Users, FileText, CalendarDays, CreditCard, Scale, ShieldCheck } from "lucide-react";

export default function AppShell({ children, hideFooter = false }) {
  const { user, signOut } = useAuth();
  const { lang } = useLang();
  const navigate = useNavigate();

  const items = [
    { to: "/dashboard", label: { en: "Dashboard", fr: "Tableau de bord", ar: "اللوحة" }[lang], icon: LayoutGrid, testid: "nav-dashboard" },
    { to: "/chat", label: t(lang, "chat.title"), icon: MessageSquare, testid: "nav-chat" },
    { to: "/knowledge", label: t(lang, "kb.title"), icon: BookOpen, testid: "nav-knowledge" },
    { to: "/contracts", label: t(lang, "nav_ext.templates"), icon: FileText, testid: "nav-contracts" },
    { to: "/calendar", label: t(lang, "nav_ext.calendar"), icon: CalendarDays, testid: "nav-calendar" },
    { to: "/team", label: t(lang, "nav_ext.team"), icon: Users, testid: "nav-team" },
    { to: "/bookmarks", label: t(lang, "bookmarks.title"), icon: Bookmark, testid: "nav-bookmarks" },
    { to: "/lawfirm", label: t(lang, "nav_ext.lawfirm"), icon: Scale, testid: "nav-lawfirm" },
    { to: "/pricing", label: t(lang, "nav_ext.pricing"), icon: CreditCard, testid: "nav-pricing" },
    { to: "/settings", label: t(lang, "settings.title"), icon: Settings, testid: "nav-settings" },
    ...(user?.role === "admin" ? [{ to: "/admin", label: { en: "Tester access", fr: "Accès testeurs", ar: "وصول المختبِرين" }[lang], icon: ShieldCheck, testid: "nav-admin" }] : []),
  ];

  return (
    <div className="min-h-screen bg-[#F8F9FA] flex flex-col">
      <header className="border-b border-[#E9ECEF] bg-white">
        <div className="max-w-7xl mx-auto flex items-center justify-between px-4 md:px-8 h-16">
          <Link to="/dashboard" className="flex items-center gap-3" data-testid="brand-link">
            <span className="inline-block w-2 h-6 bg-[#C65D47]" />
            <span className="font-display text-xl">Law Pilot</span>
          </Link>
          <div className="flex items-center gap-3">
            <LanguageSwitcher />
            {user && (
              <div className="hidden md:flex items-center gap-3 ps-3 border-s border-[#E9ECEF]">
                <div className="text-xs text-end">
                  <div className="font-medium text-[#0A1128]">{user.name}</div>
                  <div className="text-[#868E96]">{user.company || user.email}</div>
                </div>
                <button
                  onClick={async () => { await signOut(); navigate("/"); }}
                  className="p-2 hover:bg-[#F1F3F5] transition-colors"
                  data-testid="sign-out-btn"
                  title="Sign out"
                >
                  <LogOut className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      <div className="flex-1 max-w-7xl w-full mx-auto px-4 md:px-8 py-6 grid grid-cols-12 gap-6">
        <aside className="col-span-12 md:col-span-3 lg:col-span-2">
          <nav className="bg-white border border-[#E9ECEF] sticky top-6">
            <ul>
              {items.map((it) => (
                <li key={it.to}>
                  <NavLink
                    to={it.to}
                    data-testid={it.testid}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-4 py-3 text-sm transition-colors border-s-2 ${
                        isActive
                          ? "border-s-[#C65D47] bg-[#F8F9FA] text-[#0A1128] font-medium"
                          : "border-s-transparent text-[#495057] hover:bg-[#F8F9FA]"
                      }`
                    }
                  >
                    <it.icon className="w-4 h-4" />
                    <span>{it.label}</span>
                  </NavLink>
                </li>
              ))}
            </ul>
          </nav>
        </aside>

        <main className="col-span-12 md:col-span-9 lg:col-span-10">{children}</main>
      </div>

      {!hideFooter && (
        <footer className="mt-auto border-t border-[#E9ECEF] bg-white">
          <div className="max-w-7xl mx-auto px-4 md:px-8 py-4 text-xs text-[#868E96] flex flex-col md:flex-row gap-1 md:gap-0 md:justify-between">
            <span>{t(lang, "common.disclaimer")}</span>
            <span>{t(lang, "footer")}</span>
          </div>
        </footer>
      )}
    </div>
  );
}
